import { Component, HostListener, OnInit } from '@angular/core';
import { PetServices } from '../../../../services/petservices';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-clientsbookingsmoblie',
  templateUrl: './clientsbookingsmoblie.component.html',
  styleUrl: './clientsbookingsmoblie.component.scss'
})
export class ClientsbookingsmoblieComponent implements OnInit {

  contentReady = false;
  noData = false;

  allData: any[] = [];
  total = 0;
  page = 1;
  perPage = 10;
  isLoadingMore = false;
  hasMore = true;

  searchKey = '';
  searched = false;

  showDeferredPopup = false;
  selectedBooking: any = null;
  deferredBookingDate = '';
  isUpdatingStatus = false;
  isSubAdmin = false;

  todayDateTime = this.formatDateTimeForInput(new Date());

  statusMap: any = {
    0: { label: 'pending', class: 'pending' },
    1: { label: 'approved', class: 'approved' },
    2: { label: 'rejected', class: 'rejected' },
    3: { label: 'deferred', class: 'deferred' },
    4: { label: 'completed', class: 'completed' },
  };

  petTypeMap: any = {
    cat: 'cat',
    dog: 'dog'
  };

  constructor(
    private _petServices: PetServices,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadUserRole();
    this.getBookings(true);
  }

  private loadUserRole(): void {
    this.userService.getAccountType().subscribe({
      next: (data) => {
        this.isSubAdmin = String(data?.user_type) === '6';
      },
      error: () => {
        this.isSubAdmin = false;
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    if (
      this.contentReady ||
      this.isLoadingMore ||
      !this.hasMore ||
      this.noData ||
      this.showDeferredPopup
    ) {
      return;
    }

    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const bottomReached = scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  trackByBookingId(index: number, booking: any): number {
    return booking?.id || index;
  }

  normalizePhoneInput(): void {
    this.searchKey = String(this.searchKey || '').replace(/\D/g, '');
  }

  getClientName(booking: any): string {
    return booking?.client_name || booking?.user?.name || '—';
  }

  getServiceName(booking: any): string {
    return (
      booking?.pet_service?.name_ar ||
      booking?.petService?.name_ar ||
      booking?.pet_service?.name_en ||
      booking?.petService?.name_en ||
      '—'
    );
  }

  getPetTypeLabel(petType: any): string {
    if (!petType) return '';

    const key = String(petType).trim().toLowerCase();

    return this.petTypeMap[key] || 'pet_type.other';
  }

  getBookings(reset: boolean = true): void {
    if (reset) {
      this.contentReady = true;
      this.isLoadingMore = false;
      this.page = 1;
      this.allData = [];
      this.hasMore = true;
    } else {
      this.isLoadingMore = true;
    }

    const phone = this.searchKey.trim();

    const request$ = this.searched && phone
      ? this._petServices.searchBookingsByPhone(phone, this.page, this.perPage)
      : this._petServices.getAllBookings(this.page, this.perPage);

    request$.subscribe({
      next: (res: any) => {
        const newData = res?.data || [];

        this.total = Number(res?.total || 0);

        if (reset) {
          this.allData = newData;
        } else {
          this.allData = [...this.allData, ...newData];
        }

        this.noData = this.allData.length === 0;
        this.hasMore = newData.length > 0 && this.allData.length < this.total;

        this.contentReady = false;
        this.isLoadingMore = false;
      },
      error: (err) => {
        console.error(err);

        if (!reset && this.page > 1) {
          this.page--;
        }

        this.contentReady = false;
        this.isLoadingMore = false;

        if (this.allData.length === 0) {
          this.noData = true;
        }
      }
    });
  }

  loadMore(): void {
    if (
      this.contentReady ||
      this.isLoadingMore ||
      !this.hasMore ||
      this.noData
    ) {
      return;
    }

    this.page++;
    this.getBookings(false);
  }

  search(): void {
    const phone = this.searchKey.trim();

    if (!phone) {
      this.clearSearch();
      return;
    }

    this.searched = true;
    this.getBookings(true);
  }

  clearSearch(): void {
    this.searchKey = '';
    this.searched = false;
    this.getBookings(true);
  }

  onEnterPressed(): void {
    this.search();
  }


  getStatusLabel(status: any): string {
    return this.statusMap[Number(status)]?.label || 'pending';
  }

  getStatusClass(status: any): string {
    return this.statusMap[Number(status)]?.class || 'pending';
  }

  isFinalStatus(status: any): boolean {
    return [2, 4].includes(Number(status)); // 2 = rejected, 4 = completed
  }

  hasBookingActions(booking: any): boolean {
    return !this.isFinalStatus(booking?.status) || !this.isSubAdmin;
  }

  setBookingStatus(booking: any, status: number): void {
    if (this.isFinalStatus(booking?.status)) {
      return;
    }

    if (status === 3) {
      this.openDeferredDatePopup(booking);
      return;
    }

    const oldStatus = booking.status;
    const oldDeferredDate = booking.deferred_booking_date;

    booking.status = status;
    booking.deferred_booking_date = null;

    const payload: any = {
      status: Number(status)
    };

    this.isUpdatingStatus = true;

    this._petServices.updateBookingStatus(booking.id, payload).subscribe({
      next: (res: any) => {
        booking.status = Number(res?.data?.status ?? status);
        booking.deferred_booking_date = res?.data?.deferred_booking_date ?? null;
        this.isUpdatingStatus = false;
      },
      error: (err) => {
        console.error(err);

        booking.status = oldStatus;
        booking.deferred_booking_date = oldDeferredDate;
        this.isUpdatingStatus = false;

        alert(err?.error?.message || 'Failed to update booking status');
      }
    });
  }

  openDeferredDatePopup(booking: any): void {
    if (this.isFinalStatus(booking?.status)) {
      return;
    }

    this.todayDateTime = this.formatDateTimeForInput(new Date());
    this.selectedBooking = booking;

    this.deferredBookingDate = this.formatDateTimeForDateTimeLocal(
      booking.deferred_booking_date || booking.booking_date || new Date()
    );

    this.showDeferredPopup = true;
  }

  closeDeferredPopup(): void {
    if (this.isUpdatingStatus) return;

    this.showDeferredPopup = false;
    this.selectedBooking = null;
    this.deferredBookingDate = '';
  }

  submitDeferredDate(): void {
    if (!this.selectedBooking) return;

    if (!this.deferredBookingDate) {
      alert('Please select deferred date and time');
      return;
    }

    const booking = this.selectedBooking;

    const oldStatus = booking.status;
    const oldDeferredDate = booking.deferred_booking_date;

    const cleanDateTime = this.convertDateTimeLocalToMysql(this.deferredBookingDate);

    const payload: any = {
      status: 3,
      deferred_booking_date: cleanDateTime
    };

    booking.status = 3;
    booking.deferred_booking_date = cleanDateTime;

    this.isUpdatingStatus = true;

    this._petServices.updateBookingStatus(booking.id, payload).subscribe({
      next: (res: any) => {
        booking.status = Number(res?.data?.status ?? 3);
        booking.deferred_booking_date =
          res?.data?.deferred_booking_date ?? cleanDateTime;

        this.isUpdatingStatus = false;
        this.closeDeferredPopup();
      },
      error: (err) => {
        console.error(err);

        booking.status = oldStatus;
        booking.deferred_booking_date = oldDeferredDate;
        this.isUpdatingStatus = false;

        alert(err?.error?.message || 'Failed to update deferred date');
      }
    });
  }

  formatDateTimeForDateTimeLocal(value: any): string {
    if (!value) {
      return this.formatDateTimeForInput(new Date());
    }

    let cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim();

    const mysqlMatch = cleanValue.match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/
    );

    if (mysqlMatch) {
      return `${mysqlMatch[1]}-${mysqlMatch[2]}-${mysqlMatch[3]}T${mysqlMatch[4]}:${mysqlMatch[5]}`;
    }

    const usMatch = cleanValue.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
    );

    if (usMatch) {
      const month = usMatch[1].padStart(2, '0');
      const day = usMatch[2].padStart(2, '0');
      const year = usMatch[3];

      let hours = Number(usMatch[4]);
      const minutes = usMatch[5];
      const ampm = usMatch[6].toUpperCase();

      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;

      return `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${minutes}`;
    }

    return this.formatDateTimeForInput(new Date());
  }

  formatDateTimeForInput(dateValue: any): string {
    const date = new Date(dateValue);

    if (isNaN(date.getTime())) {
      return this.formatDateTimeForInput(new Date());
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  convertDateTimeLocalToMysql(dateTimeLocal: string): string {
    if (!dateTimeLocal) return '';

    const normalized = dateTimeLocal.replace('T', ' ');

    if (normalized.length === 16) {
      return `${normalized}:00`;
    }

    return normalized;
  }

  formatDateTimeView(value: any): string {
    if (!value) return '—';

    let cleanValue = String(value);

    cleanValue = cleanValue
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0];

    const parts = cleanValue.split(' ');
    const datePart = parts[0];
    const timePart = parts[1];

    if (!datePart || !timePart) return cleanValue;

    const [hoursRaw, minutes] = timePart.split(':');
    let hours = Number(hoursRaw);

    if (isNaN(hours)) return cleanValue;

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;

    const formattedHours = String(hours).padStart(2, '0');

    return `${datePart} ${formattedHours}:${minutes} ${ampm}`;
  }

  deleteBooking(id: number): void {
    if (this.isSubAdmin) return;

    if (!confirm('Are you sure you want to delete this booking?')) return;

    this._petServices.deleteBooking(id).subscribe({
      next: () => this.getBookings(true),
      error: (err) => {
        console.error(err);
        alert(err?.error?.message || 'Failed to delete booking');
      }
    });
  }
}
